/** Client-seitige Variablen-Optionen (Spiegel zu server/templateVariableOptions.js) */

export const DEFAULT_VAR_OPTIONS = {
  prefix: '',
  suffix: '',
  transform: 'none',
  nameFormat: 'full',
  anredeMale: 'Sehr geehrter Herr {nachname}',
  anredeMaleEmpty: 'Sehr geehrter Herr',
  anredeFemale: 'Sehr geehrte Frau {nachname}',
  anredeFemaleEmpty: 'Sehr geehrte Frau',
  anredeUnknown: 'Sehr geehrte Damen und Herren',
  notes: '',
};

export function parseVarOptions(raw) {
  let parsed = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    parsed = raw;
  } else if (typeof raw === 'string' && raw.trim()) {
    try {
      parsed = JSON.parse(raw) || {};
    } catch (_) {
      parsed = {};
    }
  }
  return { ...DEFAULT_VAR_OPTIONS, ...parsed };
}

export function serializeVarOptions(options) {
  const o = parseVarOptions(options);
  return {
    prefix: String(o.prefix || ''),
    suffix: String(o.suffix || ''),
    transform: ['none', 'upper', 'lower', 'capitalize'].includes(o.transform) ? o.transform : 'none',
    nameFormat: ['full', 'first', 'last'].includes(o.nameFormat) ? o.nameFormat : 'full',
    anredeMale: String(o.anredeMale || DEFAULT_VAR_OPTIONS.anredeMale),
    anredeMaleEmpty: String(o.anredeMaleEmpty || DEFAULT_VAR_OPTIONS.anredeMaleEmpty),
    anredeFemale: String(o.anredeFemale || DEFAULT_VAR_OPTIONS.anredeFemale),
    anredeFemaleEmpty: String(o.anredeFemaleEmpty || DEFAULT_VAR_OPTIONS.anredeFemaleEmpty),
    anredeUnknown: String(o.anredeUnknown || DEFAULT_VAR_OPTIONS.anredeUnknown),
    notes: String(o.notes || ''),
  };
}

function extractFirstName(fullName = '') {
  const cleaned = String(fullName || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  if (cleaned.includes(',')) {
    const after = cleaned.split(',').slice(1).join(',').trim();
    return after.split(' ').filter(Boolean)[0] || '';
  }
  const titleRe =
    /^(dr\.?|prof\.?|dipl\.?-?(?:ing\.?(?:\s*\([a-z]+\))?|kffr?\.?|kfm\.?|bw\.?|betriebswirt(?:in)?|oec\.?|[a-zäöü.]{2,20})?\.?|ing\.?|mag\.?|med\.?|herr|frau|mr\.?|mrs\.?|ms\.?)$/i;
  const parts = cleaned.split(' ').filter(Boolean);
  const withoutTitles = parts.filter((p) => {
    const t = p.replace(/,$/, '');
    if (titleRe.test(t)) return false;
    if (/^dipl\.?-?/i.test(t) && t.length > 5) return false;
    return true;
  });
  const tokens = withoutTitles.length ? withoutTitles : parts;
  return tokens[0] || '';
}

export function applyNameFormat(fullName, format, extractLastNameFn) {
  const name = String(fullName || '').trim();
  if (!name) return '';
  if (format === 'first') return extractFirstName(name);
  if (format === 'last') return extractLastNameFn(name);
  return name;
}

function fillAnredeTemplate(tpl, contact, extractLastNameFn) {
  const name = String(contact?.name || '').trim();
  const nachname = extractLastNameFn(name);
  const vorname = extractFirstName(name);
  return String(tpl || '')
    .replace(/\{nachname\}/gi, nachname)
    .replace(/\{vorname\}/gi, vorname)
    .replace(/\{name\}/gi, name);
}

function buildSinglePersonAnrede(person, options, extractLastNameFn, normalizeGenderFn) {
  const o = parseVarOptions(options);
  const name = String(person?.name || '').trim();
  if (!name) return '';

  const gender = normalizeGenderFn
    ? normalizeGenderFn(person?.gender)
    : String(person?.gender || 'unknown').toLowerCase();
  const g = gender === 'm' ? 'male' : gender === 'f' ? 'female' : gender;
  const lastName = extractLastNameFn(name);
  if (g === 'male') {
    return fillAnredeTemplate(lastName ? o.anredeMale : o.anredeMaleEmpty, person, extractLastNameFn);
  }
  if (g === 'female') {
    return fillAnredeTemplate(lastName ? o.anredeFemale : o.anredeFemaleEmpty, person, extractLastNameFn);
  }
  return fillAnredeTemplate(o.anredeUnknown, person, extractLastNameFn);
}

function joinDualAnrede(first, second) {
  const a = String(first || '').trim();
  const b = String(second || '').trim();
  if (!a) return b;
  if (!b) return a;
  if (a.toLowerCase() === b.toLowerCase()) return a;
  const bJoined = /^Sehr\s/i.test(b) ? b.replace(/^Sehr\s/i, 'sehr ') : b;
  return `${a}, ${bJoined}`;
}

export function formatContactDisplayName(contact, options, extractLastNameFn) {
  const o = parseVarOptions(options);
  const primary = applyNameFormat(contact?.name || '', o.nameFormat, extractLastNameFn);
  const secondaryRaw = String(contact?.name2 || '').trim();
  if (!secondaryRaw) return primary;
  const secondary = applyNameFormat(secondaryRaw, o.nameFormat, extractLastNameFn);
  if (!primary) return secondary;
  if (!secondary) return primary;
  return `${primary} und ${secondary}`;
}

export function buildAnredeFromOptions(contact, options, extractLastNameFn, normalizeGenderFn) {
  const o = parseVarOptions(options);
  const name = String(contact?.name || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const company = String(contact?.company || '').trim().replace(/\s+/g, ' ').toLowerCase();
  // Firma als Kontaktname → immer neutrale Anrede („Damen und Herren“)
  if (name && company && name === company) {
    return fillAnredeTemplate(o.anredeUnknown, contact, extractLastNameFn);
  }

  const first = buildSinglePersonAnrede(
    { name: contact?.name, gender: contact?.gender },
    options,
    extractLastNameFn,
    normalizeGenderFn
  );
  const name2 = String(contact?.name2 || '').trim();
  if (!name2) return first;

  const second = buildSinglePersonAnrede(
    { name: name2, gender: contact?.gender2 },
    options,
    extractLastNameFn,
    normalizeGenderFn
  );
  return joinDualAnrede(first, second);
}

function applyTransform(value, transform) {
  const text = String(value || '');
  if (transform === 'upper') return text.toUpperCase();
  if (transform === 'lower') return text.toLowerCase();
  if (transform === 'capitalize') {
    return text.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  }
  return text;
}

export function finalizeVariableValue(baseValue, options) {
  const o = parseVarOptions(options);
  const transformed = applyTransform(baseValue, o.transform);
  return `${o.prefix || ''}${transformed}${o.suffix || ''}`;
}

/**
 * Abmeldezeile für Vorlagen-Vorschau / Kampagnen.
 * format: 'html' | 'markdown' | 'text' (Default: markdown)
 */
export function buildUnsubscribeFooter(
  mailboxEmail,
  companyName = 'BauOS',
  unsubscribeUrl = '',
  publicBaseUrl = '',
  options = {}
) {
  void mailboxEmail;
  void companyName;
  const format =
    options.format === 'html' || options.format === 'text' || options.format === 'markdown'
      ? options.format
      : 'markdown';
  const linkLabel = 'Hier können Sie sich jederzeit abmelden.';

  const resolveUrl = () => {
    const raw = String(unsubscribeUrl || '').trim();
    if (raw && (/^https?:\/\//i.test(raw) || raw.startsWith('/'))) {
      if (raw.startsWith('/') && publicBaseUrl && /^https?:\/\//i.test(String(publicBaseUrl).trim())) {
        return `${String(publicBaseUrl).trim().replace(/\/+$/, '')}${raw}`;
      }
      return raw;
    }
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? String(window.location.origin).replace(/\/+$/, '')
        : '';
    const base = String(publicBaseUrl || origin || '')
      .trim()
      .replace(/\/+$/, '');
    if (base && /^https?:\/\//i.test(base)) {
      return `${base}/bauos-Newsletter`;
    }
    return '/bauos-Newsletter';
  };

  const url = resolveUrl();
  const escapeAttr = (v) =>
    String(v || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const escapeText = (v) =>
    String(v || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  if (format === 'html') {
    const href = /^https?:\/\//i.test(url) || url.startsWith('/') ? url : '#';
    return (
      `Sie möchten künftig keine E-Mails mehr erhalten? ` +
      `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" ` +
      `style="color:#2563eb;text-decoration:underline;">${escapeText(linkLabel)}</a>`
    );
  }
  if (format === 'text') {
    return `Sie möchten künftig keine E-Mails mehr erhalten? ${linkLabel} ${url}`;
  }
  return `Sie möchten künftig keine E-Mails mehr erhalten? [${linkLabel}](${url})`;
}

export const UNSUBSCRIBE_FOOTER_SOURCE = 'system:unsubscribe_footer';
export const UNSUBSCRIBE_FOOTER_KEY = 'unsubscribe_footer';

export function isUnsubscribeFooterVar(rowOrKey, source = null) {
  const key = String(rowOrKey?.key != null ? rowOrKey.key : rowOrKey || '')
    .trim()
    .toLowerCase();
  const src = String(source || rowOrKey?.source || '').trim();
  return key === UNSUBSCRIBE_FOOTER_KEY || src === UNSUBSCRIBE_FOOTER_SOURCE;
}
